import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import { getExchangeRate } from '@/util/exchangeRate';
import { usePostForeignCurrency } from '@/hooks/usePostForeignCurrency';
import FXReceiptSuccessModal from './FXReceiptSuccessModal';
import ICON_JPN from '@/assets/flag/icon_JPN.png';
import ICON_KOR from '@/assets/flag/icon_KOR.png';
import ICON_JPN_CHECKED from '@/assets/flag/icon_JPN_checked.png';
import ICON_KOR_CHECKED from '@/assets/flag/icon_KOR_checked.png';

function ReceiptCalculator() {
  const [amount, setAmount] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(getExchangeRate());
  const [hasError, setHasError] = useState(false);
  const [currency, setCurrency] = useState<'JPY' | 'KRW'>('JPY');

  const { createForeignCurrencyAsync, isLoading } = usePostForeignCurrency({
    onSuccess: () => {
      setIsSuccessModalOpen(true);
      // 성공 후 금액 초기화
      setAmount('');
      setHasError(false);
    },
    onError: (error) => {
      console.error('Foreign currency registration error:', error);
      alert('외화 수납 등록에 실패했습니다. 다시 시도해주세요.');
    },
  });

  // 환율이 변경될 수 있으므로 주기적으로 체크
  useEffect(() => {
    const handleStorageChange = () => {
      setExchangeRate(getExchangeRate());
    };

    // storage 이벤트 리스너 추가 (다른 탭에서 변경 시)
    window.addEventListener('storage', handleStorageChange);

    // 컴포넌트 포커스 시 환율 재확인
    const interval = setInterval(() => {
      setExchangeRate(getExchangeRate());
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value) {
      setAmount(Number(value).toLocaleString());
      setHasError(false);
    } else {
      setAmount('');
      setHasError(false);
    }
  };

  const calculateRefund = (amount: string) => {
    const numAmount = Number(amount.replace(/,/g, ''));
    if (currency === 'KRW') {
      return numAmount;
    }
    // 100엔당 exchangeRate원으로 환산
    const krwAmount = Math.floor((numAmount / 100) * exchangeRate);
    return krwAmount;
  };

  const handleSubmit = async () => {
    // 입력 금액 검증
    const inputAmount = Number(amount.replace(/,/g, ''));
    if (!inputAmount || inputAmount <= 0) {
      setHasError(true);
      return;
    }

    // 환산 금액 계산
    const krwAmount = calculateRefund(amount);
    const jpyAmount = currency === 'JPY' ? inputAmount : 0;

    try {
      await createForeignCurrencyAsync({
        exchangeRate: currency === 'JPY' ? exchangeRate : 0,
        amount: krwAmount,
        jpyAmount,
      });
    } catch (error) {
      // 에러는 onError 콜백에서 처리됨
    }
  };

  return (
    <ReceiptCalculatorWrapper>
      <Wrapper>
        <CardHeader>
          <CardTitle>
            수납 금액
            {currency === 'JPY' && (
              <CardSubtitle>
                외화환율 환율 100엔 = {exchangeRate.toLocaleString()}원
              </CardSubtitle>
            )}
          </CardTitle>
        </CardHeader>
        <AmountSection>
          <RadioButtonSection>
            <RadioButton
              type='button'
              isSelected={currency === 'JPY'}
              onClick={() => setCurrency('JPY')}
            >
              <Flag src={currency === 'JPY' ? ICON_JPN_CHECKED : ICON_JPN} />
              엔화
            </RadioButton>
            <RadioButton
              type='button'
              isSelected={currency === 'KRW'}
              onClick={() => setCurrency('KRW')}
            >
              <Flag src={currency === 'KRW' ? ICON_KOR_CHECKED : ICON_KOR} />
              원화
            </RadioButton>
          </RadioButtonSection>
          <AmountInputWrapper
            hasValue={!!amount && amount !== '0'}
            hasError={hasError}
          >
            <AmountInput
              type='text'
              value={amount}
              onChange={handleAmountChange}
              placeholder='금액을 입력해주세요'
            />
            <Currency>{currency === 'JPY' ? '엔(¥)' : '원(₩)'}</Currency>
          </AmountInputWrapper>
        </AmountSection>
        {hasError && <ErrorMessage>금액을 입력해주세요</ErrorMessage>}
      </Wrapper>
      <RefundInfo>
        현금 영수증 발행 금액 : {calculateRefund(amount).toLocaleString()} 원
      </RefundInfo>

      <SubmitButton onClick={handleSubmit} disabled={isLoading || hasError}>
        {isLoading ? '등록 중...' : '수납 등록'}
      </SubmitButton>

      <FXReceiptSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </ReceiptCalculatorWrapper>
  );
}
export default ReceiptCalculator;

const ReceiptCalculatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 36px;
  border-radius: 12px;
  background: #fff;
`;

const CardHeader = styled.div`
  display: flex;
  width: 200px;
`;

const CardTitle = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--Mono-80, #3a3b3e);
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 36px */
  letter-spacing: -0.48px;
`;

const CardSubtitle = styled.span`
  color: var(--Mono-35, #a7a7a7);
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 18px */
  letter-spacing: -0.24px;
`;
const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RadioButtonSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 12px;
`;

const RadioButton = styled.button<{ isSelected: boolean }>`
  box-sizing: border-box;
  padding: 4px 12px 4px 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 20px;
  border: 2px solid ${(props) => (props.isSelected ? '#246CF6' : '#BBB')};
  background: ${(props) => (props.isSelected ? '#7CA7FA' : '#fff')};
  color: ${(props) => (props.isSelected ? '#FFFFFF' : '#3A3B3E')};
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const AmountInputWrapper = styled.div<{ hasValue: boolean; hasError: boolean }>`
  flex: 1;

  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 12px;
  border: 2px solid
    ${(props) => (props.hasError ? '#FF2D55' : 'var(--Blue-100, #246cf6)')};
  border-radius: var(--Radius-4-Input, 4px);

  background: ${(props) => (props.hasValue ? '#F4F8FF' : '#fff')};
  gap: 8px;
`;

const AmountInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  color: var(--Blue-100, #246cf6);
  text-align: right;
  /* WebHead/18_BD */
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 27px */
  background: none;

  &::placeholder {
    color: var(--Mono-35, #a7a7a7);
  }
`;

const Currency = styled.span`
  color: var(--Mono-80, #3a3b3e);
  text-align: right;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-self: flex-end;
  text-align: right;

  color: #ff2d55;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
  position: absolute;
  bottom: -26px;
  right: 0;
`;

const RefundInfo = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-self: flex-end;
  text-align: right;
  color: var(--Mono-80, #3a3b3e);
  text-align: right;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  width: 240px;
  height: 52px;
  padding: 14px 20px;
  border-radius: 8px;
  border: none;
  background: var(--Blue-100, #246cf6);
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background: #a7c4fb;
    cursor: not-allowed;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 30px;
  width: 100%;
  margin-bottom: 36px;
  position: relative;
`;

const Flag = styled.img`
  width: 24px;
  height: 24px;
`;
